import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  getReservations,
  getReservation,
  createReservation,
  editReservation,
  approveReservation,
  rejectReservation,
  getReservationHistory,
} from "../../../services";
import type {
  ReservationListParams,
  CreateReservationPayload,
  EditReservationPayload,
  ReservationHistoryParams,
} from "../../../privatePages/Reservations/types";
import { QUERY_KEYS } from "../queryKeys";

/**
 * Fetches paginated reservations for the authenticated supplier.
 * GET /v2/reservations
 */
export const useGetReservations = (params?: ReservationListParams) => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_RESERVATIONS, params ?? null],
    queryFn: () => getReservations(params),
    refetchOnWindowFocus: false,
  });
};

/**
 * Fetches a single reservation detail with items.
 * GET /v2/reservations/:id
 */
export const useGetReservation = (id: number) => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_RESERVATION, id],
    queryFn: () => getReservation(id),
    enabled: !!id,
    refetchOnWindowFocus: false,
  });
};

/**
 * Mutation to create a new pending reservation.
 * POST /v2/reservations
 * Invalidates GET_RESERVATIONS on success.
 */
export const useCreateReservation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateReservationPayload) =>
      createReservation(payload),
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.GET_RESERVATIONS]);
    },
  });
};

/**
 * Mutation to edit a pending reservation.
 * PATCH /v2/reservations/:id
 * Invalidates GET_RESERVATIONS on success.
 */
export const useEditReservation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: EditReservationPayload }) =>
      editReservation(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.GET_RESERVATIONS]);
    },
  });
};

/**
 * Mutation to approve a pending reservation (decrements stock).
 * POST /v2/reservations/:id/approve
 * Invalidates GET_RESERVATIONS and GET_INVENTORY on success.
 */
export const useApproveReservation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => approveReservation(id),
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.GET_RESERVATIONS]);
      queryClient.invalidateQueries([QUERY_KEYS.GET_INVENTORY]);
    },
  });
};

/**
 * Mutation to reject a pending reservation.
 * POST /v2/reservations/:id/reject
 * Invalidates GET_RESERVATIONS on success.
 */
export const useRejectReservation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason?: string }) =>
      rejectReservation(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.GET_RESERVATIONS]);
    },
  });
};

/**
 * Fetches paginated reservation history.
 * GET /v2/reservations/history
 */
export const useGetReservationHistory = (params?: ReservationHistoryParams) => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_RESERVATION_HISTORY, params ?? null],
    queryFn: () => getReservationHistory(params),
    refetchOnWindowFocus: false,
  });
};
